<template>
  <div class="add--part">
    <b-container fluid>
      <b-row>
        <b-col cols="12">
          <section-header title="Part Details" />
          <b-row>
            <b-col cols="2">
              <b-form-group label="Part Number" class="required">
                <vue-simple-suggest class="suggest-input form-control" :styles="autoCompleteStyle" :list="getList"
                  v-model="chosenPartNumber" :max-suggestions="20" :min-length="2" :debounce="200"
                  :filter-by-query="true" value-attribute="id" display-attribute="partNumber" @select="onSuggestSelect"
                  @input="onSuggestChange" :class="{
										'is-invalid': $v.chosenPartNumber.$error && part.id === 0,
									}" @blur="$v.chosenPartNumber.$touch()" @request-done="onRequestDone">
                    <div slot="misc-item-below" v-if="isLoadingPart" class="misc-item">
                      <span>Loading Part...</span>
                    </div>
                  </vue-simple-suggest>
                <div class="invalid-feedback">
                  <div v-if="!$v.chosenPartNumber.required && part.id === 0">
                    Part Number is required
                  </div>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Manufacturer">
                <b-form-select v-model="part[invFields.MANUFACTURER_ID]" :disabled="!!part.id">
                  <option v-for="(select, index) in sortSelect(manufacturers)" :key="index" :value="select.id">
                    {{ select.name }}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group label="Customer Part Number">
                <b-form-input v-model="part[invFields.CUSTOMER_PN]"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group label="Alternate Part Number">
                <b-form-input v-model="part[invFields.ALT_NUMBER]" :disabled="!!part.id"></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="2">
              <b-form-group label="Quantity">
                <b-form-input v-model="part[qlFields.QUANTITY]" type="number"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Target Price">
                <b-form-input v-model="part[reqFields.TARGET_PRICE]" type="number"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Condition">
                <lookup-dropdown label="condition" moduleName="conditions" resource="inventoryCondition"
                  propKey="conditionId" :value="part.conditionId" :options="conditions" @change="onValueChange" />
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Class">
                <lookup-dropdown label="class" moduleName="classes" resource="inventoryClass" propKey="classId"
                  :value="part.classId" :options="classes" @change="onValueChange" :dropdownDisabled="!!part.id" />
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group label="Description">
                <b-form-input v-model="part[invFields.DESCRIPTION]" :disabled="!!part.id">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="2">
              <b-form-group label="Converted Price">
                <b-form-input type="number" v-model="part[reqFields.CONVERTED_TARGET_PRICE]">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Buy Price">
                <b-form-input type="number" v-model="part[reqFields.BUY_PRICE]"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Packaging">
                <lookup-dropdown label="packaging" moduleName="packaging" resource="inventoryPackaging"
                  propKey="packageId" :value="part.packageId" :options="packaging" @change="onValueChange"
                  :dropdownDisabled="!!part.id" />
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Ship Via">
                <b-form-select v-model="part[reqFields.SHIP_VIA_ID]">
                  <option v-for="(select, index) in sortSelect(shipViaList)" :key="index" :value="select.id">
                    {{ select.name }}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Temp">
                <b-form-input v-model="part[reqFields.TEMP]" :disabled="!!part.id"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Lead free">
                <b-form-select v-model="part[invFields.LEAD_FREE]" id="leadFree" size="sm">
                  <option v-for="value in leadFreeValues" v-bind:key="value" v-bind:value="value">
                    {{ value }}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="2">
              <b-form-group label="Date Code">
                <b-form-input v-model="part[invFields.DATE_CODE]"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Lot Number">
                <b-form-input v-model="part[reqFields.LOT_NUMBER]"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Revision">
                <b-form-input v-model="part[invFields.REVISION]"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Needs By">
                <date-picker v-model="part.needsBy" :config="datePickerOptions"></date-picker>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group label="Testing">
                <b-form-select v-model="part[invFields.TESTING_ID]">
                  <option v-for="(select, index) in sortSelect(testings)" :key="index" :value="select.id">
                    {{ select.name }}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="4">
              <b-form-group label="Request Note">
                <b-textarea v-model="part[reqFields.REQUEST_NOTE]"></b-textarea>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group label="Comment">
                <b-textarea v-model="part[reqFields.COMMENT]"></b-textarea>
              </b-form-group>
            </b-col>
            <b-col cols="4 mt-5">
              <required-descriptor />
            </b-col>
          </b-row>
          <!--<div class="buttons-panel">
                <button type="button" class="btn btn-secondary" @click="redirectToList">Back to list</button>
                <button type="button" class="btn btn-secondary" @click="clearPartDetails">Cancel</button>
                <button type="button" class="btn btn-primary" @click="savePartAndNew">Save & New</button>
                <button type="button" class="btn btn-primary" @click="savePartAndFinish">Save & Finish</button>
              </div>-->
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import VueSimpleSuggest from 'vue-simple-suggest';
  import { required } from 'vuelidate/lib/validators';
  import moment from 'moment';

  import { DATE_FORMAT, AUTO_COMPLETE_STYLES, LEAD_FREE_VALUES, US_DOLLARS_CURRENCY_ID } from '@/shared/constants';
  //import { CRFQ_PART_STATUSES } from '@/pages/Requirements/constants';
  import { FIELDS as INVENTORY_FIELDS } from '@/pages/Inventory/components/InventoryDetails/constants';
  import { FIELDS as QUOTE_LINE_FIELDS } from '@/pages/Quotes/components/QuoteLines/constants';
  import { FIELDS as REQUIREMENT_FIELDS } from './constants';

  import companyEditDto from '@/pages/Company/components/EditCompany/dto';
  //import QuoteDto from '@/pages/Quotes/components/QuoteLines/dto';

  import partService from './service';
  //import service from '@/pages/Quotes/components/QuoteLines/service';
  import contactService from '@/resources/ContactService';
  //import inventoryService from '@/pages/Inventory/service';
  import companyService from '@/pages/Company/service';
  import { eventBus } from '@/main';

  const COMPANY_CUSTOMER_TYPE_ID = 1;

  export default {
    components: {
      VueSimpleSuggest,
    },
    data() {
      return {
        header: {
          accountId: null,
          [REQUIREMENT_FIELDS.RFQ_DATE]: moment(),
          [REQUIREMENT_FIELDS.REQ_FAX]: false,
          [REQUIREMENT_FIELDS.CURRENCY_ID]: US_DOLLARS_CURRENCY_ID,
        },
        newContact: {},
        chosenName: '',
        customerNameTouched: false,
        contactFirstNameTouched: false,
        contactLastNameTouched: false,
        selectedAccount: {},
        datePickerOptions: {
          format: DATE_FORMAT,
          useCurrent: false,
        },
        autoCompleteStyle: AUTO_COMPLETE_STYLES,
        DATE_FORMAT,
        moment,
        part: {
          id: 0,
          classId: 0,
          conditionId: 0,
          packageId: 0,
        },
        chosenPartNumber: null,
        selectedPart: {},
        testings: [],
        invFields: INVENTORY_FIELDS,
        reqFields: REQUIREMENT_FIELDS,
        qlFields: QUOTE_LINE_FIELDS,
        leadFreeValues: LEAD_FREE_VALUES,
        partAdded: false,
        isLoadingPart: false,
      };
    },
    validations: {
      chosenPartNumber: { required },
      header: {
        [REQUIREMENT_FIELDS.ACCOUNT_ID]: { required },
        [REQUIREMENT_FIELDS.CONTACT_ID]: { required },
        [REQUIREMENT_FIELDS.RFQ_DATE]: { required },
      },
    },
    computed: {
      ...mapGetters('requirementAdd', [
        'partNumbers',
        'shipViaList',
        'currentPart',
        'customers',
        'contacts',
        'rfpHeader',
      ]),
      ...mapGetters('lookup', [
        'classes',
        'manufacturers',
        'conditions',
        'packaging',
        'referralSources',
        'currencies',
      ]),
      customerId() {
        return this.$route.params.customerId;
      },
      companyId() {
        return this.$route.params.companyId;
      },
    },
    watch: {
      currentPart: function () {
        this.part = this.currentPart;
        this.chosenPartNumber = this.part.partNumber;
        this.selectedPart = this.currentPart;
        eventBus.$emit('chgPartId', { id: this.part.id, companyId: this.companyId });
        if (this.part.needsBy) {
          this.part.needsBy = moment(this.currentPart.needsBy).format(
            DATE_FORMAT
          );
        }
      },
    },
    methods: {
      ...mapActions('requirementAdd', [
        'getPartNumbers',
        'getShipViaList',
        'getPartById',
        'clearCurrentPart',
        'getCustomers',
        'getContacts',
        'clearContacts',
        'addRfp',
        'getRfpHeaderById',
        'getCompanyById',
      ]),
      /*...mapActions('requirements', ['getPartsForRequirement']),*/
      onValueNameChange({ name, value }) {
        this.header[name] = value;
      },
      async onSuggestNameSelect(suggestion) {
        this.selectedAccount = suggestion;
        this.header[REQUIREMENT_FIELDS.ACCOUNT_ID] = suggestion.id;
        this.newContact = {};
        await this.getContacts(suggestion.id);
      },
      async onSuggestNameChange() {
        if (
          this.selectedAccount.name &&
          this.chosenName !== this.selectedAccount.name
        ) {
          this.header[REQUIREMENT_FIELDS.ACCOUNT_ID] = null;
          this.header[REQUIREMENT_FIELDS.CONTACT_ID] = null;
          this.$v.header[REQUIREMENT_FIELDS.ACCOUNT_ID].$touch();
          await this.clearContacts();
        }
      },
      async onSuggestBlur() {
        this.customerNameTouched = true;
      },
      async onFirstNameBlur() {
        this.contactFirstNameTouched = true;
      },
      async onLastNameBlur() {
        this.contactLastNameTouched = true;
      },
      async getListOfNames(name) {
        await this.getCustomers(name);
        return this.customers;
      },
      onValueChange({ name, value }) {
        this.part[name] = value;
      },
      async onSuggestSelect(suggestion) {
        this.selectedPart = suggestion;
        await this.getPartById(suggestion.id);
      },
      async onSuggestChange() {
        this.isLoadingPart = true;
        if (
          this.selectedPart.partNumber &&
          this.chosenPartNumber !== this.selectedPart.partNumber
        ) {
          this.part = { id: 0 };
        }
      },
      async getList(partNumber) {
        await this.getPartNumbers(partNumber);
        return this.partNumbers;
      },
      onRequestDone() {
        this.isLoadingPart = false;
      },
      async clearPartDetails() {
        await this.clearCurrentPart();
        this.part = { id: 0, classId: 0, conditionId: 0, packageId: 0 };
        this.chosenPartNumber = '';
        this.$v.chosenPartNumber.$reset();
      },
      redirectToList() {
        this.$router.push({
          name: 'requirements',
        });
      },
      async saveHeader() {
        this.customerNameTouched = true;
        this.contactFirstNameTouched = true;
        this.contactLastNameTouched = true;

        this.$v.header[REQUIREMENT_FIELDS.RFQ_DATE].$touch();
        if (this.$v.header[REQUIREMENT_FIELDS.RFQ_DATE].$invalid) {
          return;
        }
        this.$v.header[REQUIREMENT_FIELDS.ACCOUNT_ID].$touch();
        if (
          this.$v.header[REQUIREMENT_FIELDS.ACCOUNT_ID].$invalid &&
          !this.chosenName
        ) {
          return;
        }
        this.$v.header[REQUIREMENT_FIELDS.CONTACT_ID].$touch();
        if (
          this.$v.header[REQUIREMENT_FIELDS.CONTACT_ID].$invalid &&
          (!this.newContact.firstName || !this.newContact.lastName)
        ) {
          return;
        }

        if (!this.header.accountId) {
          const data = new companyEditDto({
            id: 0,
            name: this.chosenName,
            accountTypeIds: [COMPANY_CUSTOMER_TYPE_ID],
          });
          const { id } = await companyService.create(data);
          this.header[REQUIREMENT_FIELDS.ACCOUNT_ID] = id;
        }
        if (!this.header.contactId) {
          const { id } = await contactService.create({
            id: 0,
            accountId: this.header[REQUIREMENT_FIELDS.ACCOUNT_ID],
            firstName: this.newContact.firstName,
            lastName: this.newContact.lastName,
          });
          this.header[REQUIREMENT_FIELDS.CONTACT_ID] = id;
        }
        const id = await partService.addRfp(this.header);
        await this.getRfpHeaderById(id);
        await this.getContacts(this.rfpHeader[REQUIREMENT_FIELDS.ACCOUNT_ID]);
        this.header = this.rfpHeader;
      },
    },
    async mounted() {
      this.getShipViaList();

      if (this.$route.params.crfqId) {
        await this.getRfpHeaderById(this.$route.params.crfqId);
        this.header = this.rfpHeader;
        this.chosenName = this.rfpHeader[REQUIREMENT_FIELDS.ACCOUNT_NAME];
        await this.getContacts(this.rfpHeader[REQUIREMENT_FIELDS.ACCOUNT_ID]);
      }

      if (this.customerId) {
        const customer = await this.getCompanyById(this.customerId);
        this.chosenName = customer.name;
        this.onSuggestNameSelect(customer);
      }
    },
    beforeDestroy() {
      this.$emit('newPart', this.part);
    },
  };
</script>

<style lang="scss">
  @import '@/assets/scss/_colors.scss';

  .add--part {
    margin: 10px 0;

    .inline-header {
      margin-bottom: 10px;
      padding-bottom: 5px;
      border-bottom: 1px solid $dirtyWhite;
      color: $mutedGray;
      font-weight: bold;

      &.margin-top {
        margin-top: 20px;
      }
    }

    .buttons-panel {
      margin-top: 10px;
      text-align: right;

      .btn {
        margin-left: 10px;
      }
    }

    .form-control.is-invalid {
      padding-right: 0px;
    }

    .vue-simple-suggest.designed {
      .input-wrapper {
        input {
          height: 27px;
          background: transparent;
          padding-right: 30px;
        }
      }
    }
  }
</style>